// Training Sessions — standalone module under Master Management.
//
// Pulled out of Employee Score (Phase 3 follow-up) so any project can manage
// trainings independent of the scorecard. The page lets admins:
//   * Create / edit / cancel training sessions (in-person, web-based with a
//     registration URL — schema already supports both via `training_type`).
//   * Assign employees to a session (employee picker with city / AM /
//     category / trade filters).
//   * Mark attendance per training (manual toggle or bulk Excel upload).
//
// Employee Score still consumes the resulting `training_attendees` rows as
// the data source for its `training_pct` system metric — but configuration
// of training sessions themselves now lives here, not on Employee Score.
//
// Implementation: thin wrapper around AppleScorecardPage in `embedded` mode,
// with the inner tab list filtered to just 'trainings' and
// 'training_attendance'. All backend endpoints (`/api/apple-scorecard/trainings`
// and friends) are already de-iPro'd via Phase 3's requireProjectId gate.

import { Award } from 'lucide-react';
import AppleScorecardPage from './AppleScorecardPage';
import CustomReportPanel from '../components/CustomReportPanel';
import { useLinkedSearch } from '../hooks/useLinkedSearch';

export default function TrainingSessionsPage() {
  // The trainings list's name box. A link can fill it (?q=<a training's name>, as
  // search does), so the page opens on the training that was picked.
  const trainingNameSearch = useLinkedSearch();
  return (
    // Wrapped so admins can build custom reports off training data (assigned vs
    // attended, no-shows, per-store breakdowns). The existing page becomes the
    // "Raw Data" tab; generated reports get their own tabs alongside it.
    <CustomReportPanel
      sourceKey="offline-training"
      sourceLabel="Offline Trainings"
      promptPlaceholder='e.g. "Attendance rate per training this month, as a bar chart"'
    >
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <Award className="h-7 w-7 text-primary-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Offline Trainings</h1>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Schedule trainings, assign employees, and record attendance. The data here feeds the
          Training Participation metric on Employee Score.
        </p>
      </div>
      {/* The embedded AppleScorecardPage suppresses its own header but keeps
          its inner tab nav — limited to Trainings + Training Attendance. */}
      {/* Single combined tab — the trainings list now has inline
          per-row expansion for attendance, so no separate attendance tab
          is needed. Locking to one tab also hides the (now redundant)
          inner tab bar. */}
      <AppleScorecardPage embedded restrictTab="trainings" trainingNameSearch={trainingNameSearch} />
    </div>
    </CustomReportPanel>
  );
}
